import { useState, useEffect, createRef } from "react";
import { Tooltip } from "recharts";
import PropTypes from "prop-types";

import { t } from "../../../services";
import { useChartData } from "../../../hooks";
import { useCustomTooltip } from "../hooks/useCustomTooltip";
import { useTransformedData } from "../hooks/useTransformedData";
import { useCalculateData } from "../hooks/useCalculateData";

import { Paragraph2 } from "../../Text/Paragraphs";
import { RadialChartTooltip } from "../components/RadialChartTooltip";

import { Line, LineChartContainer, Section, TitleWrapper, TotalDetails } from "./styled";
import FlexContainers from "../../Containers/FlexContainers";

export const LineChart = ({ data = [], metricsConfig, title, totalDetails, TooltipComponent }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [left, setLeft] = useState(0);

  const chartContainerRef = createRef();
  const { isTooltipHidden, tooltipPosition, showTooltip, hideTooltip } =
    useCustomTooltip(chartContainerRef);

  useEffect(() => {
    if (chartContainerRef?.current) {
      const rect = chartContainerRef.current.getBoundingClientRect();
      setLeft(rect?.left);
    }
  }, []);

  const { formattedData, filteredMetricsConfig, opacity } = useChartData({
    data: data,
    isBarChart: false,
    metricsConfig
  });

  const transformedData = useTransformedData(formattedData, filteredMetricsConfig, opacity);
  const totalValue = transformedData?.reduce((sum, segment) => sum + segment.value, 0) || 0;
  const calculatedData = useCalculateData(transformedData, activeIndex, totalValue);

  const formatTooltipValue = (value) => {
    return `${value.toFixed(1)}%`;
  };

  return (
    <LineChartContainer Container={FlexContainers.FlexColumnStart} gap={4}>
      <TitleWrapper Container={FlexContainers.FlexRowBetween} width={"100%"}>
        <Paragraph2>{t(title)}</Paragraph2>
        <Paragraph2>
          <span>{totalValue}</span>
          <TotalDetails>{totalDetails}</TotalDetails>
        </Paragraph2>
      </TitleWrapper>

      <Line ref={chartContainerRef} onMouseMove={showTooltip} onMouseLeave={hideTooltip}>
        {calculatedData?.map((item, index) => {
          return (
            <Section
              key={item.id}
              width={item.value}
              color={item.fill}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
            />
          );
        })}
      </Line>

      {totalValue ? (
        <Tooltip
          wrapperStyle={{
            zIndex: 2,
            display: isTooltipHidden ? "none" : "block",
            position: "fixed",
            top: "unset",
            left: left ? `${left}px` : "unset",
            visibility: "visible",
            transition: "transform 400ms",
            transform: `translate(${tooltipPosition.x}px, ${tooltipPosition.y}px)`
          }}
          content={
            TooltipComponent ? (
              TooltipComponent
            ) : (
              <RadialChartTooltip
                data={calculatedData[activeIndex]}
                total={transformedData[activeIndex]?.value}
              />
            )
          }
          position={{ x: tooltipPosition.x, y: tooltipPosition.y }}
          formatter={formatTooltipValue}
        />
      ) : null}
    </LineChartContainer>
  );
};

LineChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      metrics: PropTypes.objectOf(PropTypes.number).isRequired
    })
  ).isRequired,
  title: PropTypes.string.isRequired,
  metricsConfig: PropTypes.shape(PropTypes.object).isRequired,
  totalDetails: PropTypes.string,
  TooltipComponent: PropTypes.node
};
